/**
 * Alle ResourceRefs des Demonstrationsstands an einer Stelle.
 *
 * **Warum nicht dort, wo das jeweilige Objekt entsteht.** Der Bestand ist
 * verflochten: Ein Einsatz zeigt auf einen Auftrag, der Auftrag auf einen
 * Kunden, der Servicebericht auf beide, der Link-Graph auf alles. Würde jede
 * Datei ihre eigenen Refs bilden, entstünden entweder Importzyklen oder
 * doppelte Zeichenketten — und die zweite Variante bricht leise: Ein Tippfehler
 * in `auftrag-2481` gegen `auftrag-2418` erzeugt keinen Fehler, sondern eine
 * Verknüpfung ins Leere, die erst in der Oberfläche als fehlender Eintrag
 * auffällt.
 *
 * Hier ist jede Kennung genau einmal geschrieben. Wer sie falsch tippt,
 * bekommt einen Typfehler.
 *
 * Die Nummern sind bewusst nicht fortlaufend ab 1: Ein Betrieb, dessen erster
 * Kunde die Nummer 1 und dessen erster Auftrag die Nummer 1 trägt, sieht aus
 * wie eine leere Installation. Der Demostand soll einen laufenden Betrieb
 * zeigen.
 */

import { ref } from "./modell.js";

// ───────────────────────────── Personen ─────────────────────────────

/**
 * Personen liegen unter `provider: "portal"` und nicht unter `keycloak`.
 *
 * Keycloak steht nicht in der Provider-Liste von `04` §4.2, und das ist kein
 * Versehen: Ein ResourceRef zeigt auf eine *Ressource* in einem führenden
 * System, nicht auf eine Identität. Der Actor trägt seine Identität nach
 * `04` §4.5 getrennt; hier geht es um die Person als Stammsatz, den das Portal
 * führt — Anzeigename, Kürzel, Farbe im Kalender.
 */
export const PERSON = {
  inhaber: ref("portal", "person", "p-wagner"),
  buero: ref("portal", "person", "p-kern"),
  monteurA: ref("portal", "person", "p-liszka"),
  monteurB: ref("portal", "person", "p-reuther"),
  monteurC: ref("portal", "person", "p-demir"),
  partner: ref("portal", "person", "p-ostermann"),
} as const;

// ───────────────────────────── Kunden ─────────────────────────────

export const KUNDE = {
  baeckerei: ref("portal", "customer", "k-1042"),
  hausverwaltung: ref("portal", "customer", "k-1078"),
  autohaus: ref("portal", "customer", "k-1103"),
  privat: ref("portal", "customer", "k-1156"),
  seniorenheim: ref("portal", "customer", "k-1189"),
  metallbau: ref("portal", "customer", "k-1204"),
  gemeinde: ref("portal", "customer", "k-1233"),
  praxis: ref("portal", "customer", "k-1251"),
} as const;

// ───────────────────────────── Aufträge ─────────────────────────────

/**
 * Aufträge sind Vikunja-Projekte (`04` §4.4).
 *
 * Die `opaqueId` ist hier die Auftragsnummer und nicht die Vikunja-Projekt-ID.
 * Für einen Demostand ohne Registry ist das die ehrlichere Abbildung: Es gibt
 * keine zweite ID, die irgendwo abgelegt wäre. Sobald `LNK-001` die Registry
 * baut, wird daraus eine echte Zuordnung — und dann steht hier eine
 * undurchsichtige Kennung, wie `04` §4.2 sie verlangt.
 */
export const AUFTRAG = {
  kuehlung: ref("vikunja", "project", "a-2481"),
  treppenhaus: ref("vikunja", "project", "a-2483"),
  hebebuehne: ref("vikunja", "project", "a-2486"),
  wallbox: ref("vikunja", "project", "a-2490"),
  rufanlage: ref("vikunja", "project", "a-2492"),
  hallenbeleuchtung: ref("vikunja", "project", "a-2495"),
  strassenbeleuchtung: ref("vikunja", "project", "a-2497"),
  praxisumbau: ref("vikunja", "project", "a-2499"),
  pruefung2026: ref("vikunja", "project", "a-2501"),
  netzwerk: ref("vikunja", "project", "a-2503"),
  stoerung: ref("vikunja", "project", "a-2505"),
  zaehlerschrank: ref("vikunja", "project", "a-2507"),
} as const;

// ───────────────────────────── Einsätze ─────────────────────────────

export const EINSATZ = {
  // heute
  kuehlungHeute: ref("vikunja", "task", "e-8801"),
  treppenhausHeute: ref("vikunja", "task", "e-8802"),
  hebebuehneHeute: ref("vikunja", "task", "e-8803"),
  wallboxHeute: ref("vikunja", "task", "e-8804"),
  stoerungHeute: ref("vikunja", "task", "e-8805"),
  praxisHeute: ref("vikunja", "task", "e-8806"),
  // morgen
  rufanlageMorgen: ref("vikunja", "task", "e-8810"),
  hallenbeleuchtungMorgen: ref("vikunja", "task", "e-8811"),
  wallboxMorgen: ref("vikunja", "task", "e-8812"),
  zaehlerschrankMorgen: ref("vikunja", "task", "e-8813"),
  // später diese und nächste Woche
  strassenbeleuchtungSpaeter: ref("vikunja", "task", "e-8820"),
  praxisumbauSpaeter: ref("vikunja", "task", "e-8821"),
  pruefungSpaeter: ref("vikunja", "task", "e-8822"),
  netzwerkSpaeter: ref("vikunja", "task", "e-8823"),
  // erledigt oder in Prüfung
  kuehlungVorwoche: ref("vikunja", "task", "e-8790"),
  treppenhausVorwoche: ref("vikunja", "task", "e-8791"),
  hebebuehneVorwoche: ref("vikunja", "task", "e-8792"),
  rufanlageVorwoche: ref("vikunja", "task", "e-8793"),
  netzwerkVorwoche: ref("vikunja", "task", "e-8794"),
  // storniert
  hallenbeleuchtungStorniert: ref("vikunja", "task", "e-8795"),
} as const;

// ───────────────────────── Eingangsvorgänge ─────────────────────────

export const EINGANG = {
  rechnungElektrogrosshandel: ref("portal", "intake-item", "i-01ka01"),
  rechnungKabelwerk: ref("portal", "intake-item", "i-01ka02"),
  rechnungFehlerhaft: ref("portal", "intake-item", "i-01ka03"),
  anfrageSchule: ref("portal", "intake-item", "i-01ka04"),
  reklamationBaeckerei: ref("portal", "intake-item", "i-01ka05"),
  lieferschein: ref("portal", "intake-item", "i-01ka06"),
  pruefprotokoll: ref("portal", "intake-item", "i-01ka07"),
  angebotsanfrage: ref("portal", "intake-item", "i-01ka08"),
  wartungsvertrag: ref("portal", "intake-item", "i-01ka09"),
  verdaechtigerAnhang: ref("portal", "intake-item", "i-01ka10"),
  scanUnleserlich: ref("portal", "intake-item", "i-01ka11"),
} as const;

// ───────────────────────────── Ablage ─────────────────────────────

export const DATEI = {
  schaltplanKuehlung: ref("nextcloud", "file", "f-01n001"),
  fotoKuehlungVorher: ref("nextcloud", "file", "f-01n002"),
  fotoKuehlungNachher: ref("nextcloud", "file", "f-01n003"),
  aufmassTreppenhaus: ref("nextcloud", "file", "f-01n004"),
  datenblattLeuchte: ref("nextcloud", "file", "f-01n005"),
  fotoHebebuehne: ref("nextcloud", "file", "f-01n006"),
  planWallbox: ref("nextcloud", "file", "f-01n007"),
  anschlussschemaWallbox: ref("nextcloud", "file", "f-01n008"),
  grundrissPraxis: ref("nextcloud", "file", "f-01n009"),
  fotoZaehlerschrank: ref("nextcloud", "file", "f-01n010"),
  sprachnotizStoerung: ref("nextcloud", "file", "f-01n011"),
  berichtPdfKuehlung: ref("nextcloud", "file", "f-01n012"),
  berichtPdfTreppenhaus: ref("nextcloud", "file", "f-01n013"),
  handbuchHebebuehne: ref("nextcloud", "file", "f-01n014"),
} as const;

export const DOKUMENT = {
  rechnungElektrogrosshandel: ref("paperless", "document", "d-844"),
  rechnungKabelwerk: ref("paperless", "document", "d-845"),
  rechnungFehlerhaft: ref("paperless", "document", "d-846"),
  pruefprotokoll2025: ref("paperless", "document", "d-812"),
  wartungsvertragSeniorenheim: ref("paperless", "document", "d-798"),
  lieferschein: ref("paperless", "document", "d-847"),
  abnahmeprotokollWallbox: ref("paperless", "document", "d-830"),
} as const;

export const WISSEN = {
  fehlercodesKuehlaggregat: ref("nextcloud", "collective-page", "w-01c01"),
  wallboxAnschluss: ref("nextcloud", "collective-page", "w-01c02"),
  pruefungBetriebsmittel: ref("nextcloud", "collective-page", "w-01c03"),
  notdienstAblauf: ref("nextcloud", "collective-page", "w-01c04"),
  materialrueckgabe: ref("nextcloud", "collective-page", "w-01c05"),
} as const;

// ───────────────────────── Kommunikation ─────────────────────────

export const MAIL = {
  anfrageSchule: ref("stalwart", "email", "m-abc123"),
  reklamationBaeckerei: ref("stalwart", "email", "m-abc124"),
  rechnungElektrogrosshandel: ref("stalwart", "email", "m-abc125"),
  rechnungKabelwerk: ref("stalwart", "email", "m-abc126"),
  rechnungFehlerhaft: ref("stalwart", "email", "m-abc127"),
  terminbestaetigungPraxis: ref("stalwart", "email", "m-abc128"),
  angebotWallbox: ref("stalwart", "email", "m-abc129"),
  rueckfrageHausverwaltung: ref("stalwart", "email", "m-abc130"),
  lieferavisGrosshandel: ref("stalwart", "email", "m-abc131"),
  angebotsanfrageMetallbau: ref("stalwart", "email", "m-abc132"),
  wartungsvertragEntwurf: ref("stalwart", "email", "m-abc133"),
  verdaechtig: ref("stalwart", "email", "m-abc134"),
} as const;

export const CHATRAUM = {
  kuehlung: ref("nextcloud", "talk-room", "t-01j01"),
  stoerung: ref("nextcloud", "talk-room", "t-01j02"),
  wallbox: ref("nextcloud", "talk-room", "t-01j03"),
} as const;

export const TERMIN = {
  praxisBesprechung: ref("nextcloud", "calendar-event", "c-01e01"),
  abnahmeWallbox: ref("nextcloud", "calendar-event", "c-01e02"),
  teamrunde: ref("nextcloud", "calendar-event", "c-01e03"),
  steuerberaterin: ref("nextcloud", "calendar-event", "c-01e04"),
} as const;

// ───────────────────── Berichte, Wartung, Zugänge ─────────────────────

export const BERICHT = {
  kuehlungV1: ref("portal", "service-report", "b-01kb01"),
  kuehlungV2: ref("portal", "service-report", "b-01kb02"),
  treppenhaus: ref("portal", "service-report", "b-01kb03"),
  hebebuehne: ref("portal", "service-report", "b-01kb04"),
  rufanlage: ref("portal", "service-report", "b-01kb05"),
  netzwerk: ref("portal", "service-report", "b-01kb06"),
} as const;

export const WARTUNGSPLAN = {
  seniorenheimRufanlage: ref("portal", "maintenance-plan", "wp-01kc01"),
  autohausHebebuehne: ref("portal", "maintenance-plan", "wp-01kc02"),
  gemeindePruefung: ref("portal", "maintenance-plan", "wp-01kc03"),
} as const;

export const MAGICLINK = {
  berichtBaeckerei: ref("portal", "magiclink", "ml-01kd01"),
  abnahmeAutohaus: ref("portal", "magiclink", "ml-01kd02"),
  angebotMetallbau: ref("portal", "magiclink", "ml-01kd03"),
} as const;
